/**
 * CourseAssignments (2026-10-01) — tab "Bài tập" trên trang môn học của sinh viên:
 * bài tập (`kind=assignment`) xếp theo bài học "nên làm sau", nút "Làm bài".
 * `afterLessonId` → chỉ bài tập gợi ý sau 1 bài học (dùng cuối trang bài học).
 */
import { Button, Card, Empty, List, Space, Spin, Tag, Typography } from "antd";
import { EditOutlined, ReadOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { type QuizListItem, useQuizzes } from "@/api/assessment";

const { Text } = Typography;

function sortByLesson(a: QuizListItem, b: QuizListItem) {
  const ao = a.recommended_after_lesson?.order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.recommended_after_lesson?.order ?? Number.MAX_SAFE_INTEGER;
  return ao - bo || a.title.localeCompare(b.title);
}

export function CourseAssignments({
  courseSlug,
  afterLessonId,
  compact = false,
}: {
  courseSlug: string;
  afterLessonId?: number;
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuizzes(courseSlug);

  if (isLoading) return <Spin />;
  let items = (data ?? []).filter(
    (q) => (q.kind ?? "assignment") === "assignment" && !q.term_offering,
  );
  if (afterLessonId !== undefined) {
    items = items.filter((q) => q.recommended_after_lesson?.id === afterLessonId);
    if (items.length === 0) return null;
  }
  items = [...items].sort(sortByLesson);

  if (items.length === 0) {
    return <Empty description="Môn học chưa có bài tập nào." />;
  }

  const list = (
    <List
      dataSource={items}
      renderItem={(q) => (
        <List.Item
          actions={[
            <Button
              key="do"
              type="primary"
              size={compact ? "small" : "middle"}
              icon={<EditOutlined />}
              onClick={() => navigate(`/quizzes/${q.id}/consent`)}
            >
              Làm bài
            </Button>,
          ]}
        >
          <Space direction="vertical" size={2}>
            <Text strong>{q.title}</Text>
            {afterLessonId === undefined &&
              (q.recommended_after_lesson ? (
                <Text type="secondary">
                  <ReadOutlined /> Nên làm sau:{" "}
                  <Link to={`/lessons/${q.recommended_after_lesson.slug}`}>
                    {q.recommended_after_lesson.title}
                  </Link>
                </Text>
              ) : (
                <Tag>Bài tập chung của môn</Tag>
              ))}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {q.total_points} điểm · làm không giới hạn số lần
            </Text>
          </Space>
        </List.Item>
      )}
    />
  );

  return compact ? (
    <Card size="small" title="Bài tập nên làm sau bài học này" data-testid="lesson-assignments">
      {list}
    </Card>
  ) : (
    <div data-testid="course-assignments">{list}</div>
  );
}
